import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import api from "../api";
import { removeLocalData } from "../utils/localStorageHelper";

const CartContext = createContext(null);
const STORAGE_KEY = "cart";

function normalizeId(p) {
  return p?._id ?? p?.id;
}

// ─────────────────────────────────────────────────────────────
// Helper: check if a cart item matches a specific variant
// This is the single source of truth for variant matching.
// All functions (add, remove, update) use this same check.
// ─────────────────────────────────────────────────────────────
function isSameVariant(cartItem, productId, colorName, size) {
  const sameProduct = normalizeId(cartItem) === productId;
  const sameColor   = (cartItem?.variant?.color?.name || "") === (colorName || "");
  const sameSize    = (cartItem?.variant?.size || "")        === (size || "");
  return sameProduct && sameColor && sameSize;
}

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // Save to localStorage whenever cart changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  // Derived data
  const cartCount = useMemo(
    () => cart.reduce((sum, it) => sum + (it.quantity || 0), 0),
    [cart]
  );
  const cartTotal = useMemo(
    () =>
      cart.reduce(
        (sum, it) => sum + (Number(it.price || 0) * (it.quantity || 0)),
        0
      ),
    [cart]
  );

  // ─────────────────────────────────────────────────────────────
  // getSelectedVariant: build variant object from product data
  // ─────────────────────────────────────────────────────────────
  const getSelectedVariant = (product, selectedColor, selectedSize) => {
    if (!product?.variants) return null;

    const colorVariant = product.variants.find(
      (v) => v.color.name === selectedColor
    );
    if (!colorVariant) return null;

    const sizeVariant = colorVariant.sizes.find(
      (s) => s.size === selectedSize
    );
    if (!sizeVariant) return null;

    return {
      color: colorVariant.color,
      size: sizeVariant.size,
      originalPrice: sizeVariant.originalPrice,
      discountedPrice: sizeVariant.discountedPrice,
      stock: sizeVariant.stock,
      images: colorVariant.images,
    };
  };

  // ─────────────────────────────────────────────────────────────
  // addToCart
  // ─────────────────────────────────────────────────────────────
  const addToCart = async (product, selectedColor, selectedSize) => {
    const id = normalizeId(product);
    if (!id) return;

    const selectedVariant = getSelectedVariant(product, selectedColor, selectedSize);
    if (!selectedVariant) {
      console.error("Selected variant not found!");
      return;
    }

    const newItem = {
      ...product,
      variant: selectedVariant,
      quantity: 1,
    };

    setCart((prev) => {
      // FIX: use isSameVariant so we match product + color + size together
      const existingIndex = prev.findIndex((item) =>
        isSameVariant(item, id, selectedVariant.color.name, selectedVariant.size)
      );

      if (existingIndex !== -1) {
        // Already exists → increment quantity and move to top
        const updatedCart = [...prev];
        updatedCart[existingIndex] = {
          ...updatedCart[existingIndex],
          quantity: updatedCart[existingIndex].quantity + 1,
        };
        const updatedItem = updatedCart.splice(existingIndex, 1)[0];
        return [updatedItem, ...updatedCart];
      }

      // New variant → add to top
      return [newItem, ...prev];
    });

    try {
      const storedData = localStorage.getItem("userData");
      const token = storedData ? JSON.parse(storedData).token : null;

      const res = await api.put(
        "/user/addToCart",
        {
          productId: id,
          variant: {
            color: selectedVariant.color,
            size: selectedVariant.size,
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("Synced with backend:", res.data);
    } catch (err) {
      console.error("API error:", err.response?.data || err.message);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // removeFromCart
  // ─────────────────────────────────────────────────────────────
  const removeFromCart = async (product, selectedColor, selectedSize) => {
    const id = normalizeId(product);
    if (!id) return;

    // FIX: match directly by color name + size strings passed in,
    // no need to call getSelectedVariant (product may lack .variants)
    setCart((prev) =>
      prev.filter(
        (it) => !isSameVariant(it, id, selectedColor, selectedSize)
      )
    );

    try {
      const storedData = localStorage.getItem("userData");
      const token = storedData ? JSON.parse(storedData).token : null;

      const res = await api.put(
        "/user/remove-cart-item",
        {
          productId: id,
          variant: { color: { name: selectedColor }, size: selectedSize },
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("Removed from backend:", res.data);
    } catch (err) {
      console.error("Remove API error:", err.response?.data || err.message);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // updateQuantity — THE MAIN FIX IS HERE
  //
  // Before: matched only by product id → ALL variants updated
  // After:  matches by product id + color + size → only ONE variant updates
  // ─────────────────────────────────────────────────────────────
  const updateQuantity = async (product, selectedColor, selectedSize, qty) => {
    const id = normalizeId(product);
    if (!id) return;

    const safeQty = Math.max(1, Number(qty) || 1);

    setCart((prev) => {
      if (qty <= 0) {
        // Remove if qty reaches 0
        return prev.filter(
          (it) => !isSameVariant(it, id, selectedColor, selectedSize)
        );
      }

      return prev.map((it) => {
        // FIX: check color + size too, not just product id
        if (isSameVariant(it, id, selectedColor, selectedSize)) {
          return { ...it, quantity: safeQty };
        }
        return it; // ← all other variants are untouched
      });
    });

    try {
      const storedData = localStorage.getItem("userData");
      const token = storedData ? JSON.parse(storedData).token : null;
      if (!token) return;

      // Build variant for backend using the cart item's existing variant data
      // (product passed from AddCart already has .variant on it)
      const variantPayload = product?.variant ?? {
        color: { name: selectedColor },
        size: selectedSize,
      };

      const res = await api.post(
        "/user/changeQty",
        {
          productId: id,
          variant: variantPayload,
          newQty: safeQty,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("Quantity updated on backend:", res.data);
    } catch (err) {
      console.error("Update qty API error:", err.response?.data || err.message);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // clearCart
  // ─────────────────────────────────────────────────────────────
  const clearCart = () => {
    removeLocalData("cart");
    setCart([]);
  };

  // ─────────────────────────────────────────────────────────────
  // Sections / Gender filter
  // ─────────────────────────────────────────────────────────────
  const [sections, setSections] = useState([]);
  const [selectedGender, setSelectedGender] = useState("men");

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const res = await api.get("/user/getSections");
        setSections(res.data.data || []);
      } catch (error) {
        console.error("Failed to fetch sections:", error);
      }
    };
    fetchSections();
  }, []);

  const filteredSections = useMemo(
    () =>
      sections.filter(
        (item) => item.gender?.toLowerCase() === selectedGender
      ),
    [sections, selectedGender]
  );

  return (
    <CartContext.Provider
      value={{
        cart,
        cartCount,
        cartTotal,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        sections,
        selectedGender,
        setSelectedGender,
        filteredSections,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within <CartProvider>");
  }
  return ctx;
};